/**
 * Manages color-related state tracking for the ribbon pin customizer.
 * Tracks which colors have been manually edited by the user vs programmatically set.
 */

export interface SavedCustomState {
    colors: { colorOne: string; colorTwo: string; colorThree: string };
    text: { lineOne: string; lineTwo: string };
    textColors: { textOne: string; textTwo: string; textThree: string };
    fontSizes: { textOne: number; textTwo: number; textThree: number };
    manuallySetColors: Set<string>;
    textColorManuallySet: Set<string>;
}

export class ColorState {
    // Tracks which ribbon colors (color-1, color-2, color-3) have been manually set
    readonly manuallySetColors = new Set<string>();

    // Tracks which text colors (text-one, text-two, text-three) have been manually set
    readonly textColorManuallySet = new Set<string>();

    // Tracks text colors currently being changed programmatically (to avoid marking as manual)
    readonly programmaticTextColorChanges = new Set<string>();

    // Whether we're currently in "Custom" mode vs a preset
    isCustomMode = true;

    // Whether a color change is currently programmatic (to avoid marking as manual)
    isProgrammaticColorChange = false;

    // Saved custom state for restoring when switching back from a preset
    savedCustomState: SavedCustomState | null = null;

    /**
     * Mark a ribbon color as manually set by the user
     */
    markColorManuallySet(colorName: string): void {
        this.manuallySetColors.add(colorName);
    }

    /**
     * Check if a ribbon color has been manually set
     */
    isColorManuallySet(colorName: string): boolean {
        return this.manuallySetColors.has(colorName);
    }

    /**
     * Mark a text color as manually set by the user
     */
    markTextColorManuallySet(lineId: string): void {
        this.textColorManuallySet.add(lineId);
    }

    /**
     * Check if a text color has been manually set
     */
    isTextColorManuallySet(lineId: string): boolean {
        return this.textColorManuallySet.has(lineId);
    }

    /**
     * Check if a text color change is currently programmatic
     */
    isTextColorChangeProgrammatic(lineId: string): boolean {
        return this.programmaticTextColorChanges.has(lineId);
    }

    /**
     * Begin a programmatic text color change (won't be marked as manual)
     */
    beginProgrammaticTextColorChange(lineId: string): void {
        this.programmaticTextColorChanges.add(lineId);
    }

    /**
     * End a programmatic text color change
     */
    endProgrammaticTextColorChange(lineId: string): void {
        this.programmaticTextColorChanges.delete(lineId);
    }

    /**
     * Clear all manual tracking (for reset)
     */
    clearManualTracking(): void {
        this.manuallySetColors.clear();
        this.textColorManuallySet.clear();
    }

    /**
     * Save the current state as the custom state
     */
    saveState(state: Omit<SavedCustomState, 'manuallySetColors' | 'textColorManuallySet'>): void {
        this.savedCustomState = {
            ...state,
            manuallySetColors: new Set(this.manuallySetColors),
            textColorManuallySet: new Set(this.textColorManuallySet),
        };
    }

    /**
     * Restore the manual tracking sets from saved state
     */
    restoreManualTracking(): void {
        if (!this.savedCustomState) {
            return;
        }

        this.manuallySetColors.clear();
        for (const color of this.savedCustomState.manuallySetColors) {
            this.manuallySetColors.add(color);
        }

        this.textColorManuallySet.clear();
        for (const color of this.savedCustomState.textColorManuallySet) {
            this.textColorManuallySet.add(color);
        }
    }

    /**
     * Clear saved custom state (for reset)
     */
    clearSavedState(): void {
        this.savedCustomState = null;
    }
}
